import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config";
import { AuthContext } from "../../components/AuthContext";
import { getAuthToken } from "../../utils/authStorage";

const methodOptions = [
  { value: "all", label: "All Methods" },
  { value: "qr", label: "QR" },
  { value: "member_id", label: "Member ID" },
  { value: "rfid", label: "RFID" },
];

const DailyAttendanceReport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { role } = React.useContext(AuthContext);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [branchId, setBranchId] = useState("");
  const [disciplineId, setDisciplineId] = useState("");
  const [attendanceMethod, setAttendanceMethod] = useState("all");
  const [branches, setBranches] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const requestConfig = () => {
    const token = getAuthToken();
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [branchResponse, disciplineResponse] = await Promise.all([
          axios.get(`${config.apiBaseUrl}/get_branches`, requestConfig()),
          axios.get(`${config.apiBaseUrl}/get_disciplines`, requestConfig()),
        ]);
        setBranches(branchResponse.data.branches || []);
        setDisciplines(disciplineResponse.data.disciplines || []);
      } catch (requestError) {
        toast.error(requestError.response?.data?.error || "Failed to load report filters");
      }
    };

    loadFilters();
  }, []);

  const handleShowReport = async () => {
    if (!date) {
      setError("Please select a date");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/show-daily-attendance-report`,
        {
          date,
          branch_id: branchId || null,
          discipline: disciplineId || null,
          attendance_method: attendanceMethod,
        },
        requestConfig()
      );
      const newWindow = window.open("", "_blank");
      if (!newWindow) {
        throw new Error("Please allow pop-ups to view the report");
      }
      newWindow.document.write(response.data);
      newWindow.document.close();
      toast.success("Daily attendance report displayed successfully");
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Failed to load daily attendance");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (!date) {
      setError("Please select a date");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/export-daily-attendance-report`,
        { date, branch_id: branchId || null, discipline: disciplineId || null, attendance_method: attendanceMethod, format },
        { ...requestConfig(), responseType: "blob" }
      );
      const extension = format === "xlsx" ? "xlsx" : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `daily_attendance_${date}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.response?.data?.error || `Failed to export ${format.toUpperCase()} report`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Daily Attendance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Detailed attendance records by date and attendance method.
      </Typography>

      <Box
        display="grid"
        gap={{ xs: 1.5, sm: 2 }}
        gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" }}
        mb={2}
      >
        <TextField
          fullWidth
          type="date"
          label="Date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          size={isMobile ? "small" : "medium"}
        />
        {role === "global_admin" ? (
          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Branch</InputLabel>
            <Select value={branchId} label="Branch" onChange={(event) => setBranchId(event.target.value)}>
              <MenuItem value="">All Branches</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.branch_id} value={branch.branch_id}>{branch.branch_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Box />
        )}
        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
          <InputLabel>Discipline</InputLabel>
          <Select value={disciplineId} label="Discipline" onChange={(event) => setDisciplineId(event.target.value)}>
            <MenuItem value="">All Disciplines</MenuItem>
            {disciplines.map((discipline) => (
              <MenuItem key={discipline.discipline_id} value={discipline.discipline_id}>{discipline.discipline_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
          <InputLabel>Attendance Method</InputLabel>
          <Select value={attendanceMethod} label="Attendance Method" onChange={(event) => setAttendanceMethod(event.target.value)}>
            {methodOptions.map((method) => (
              <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<VisibilityIcon />}
          onClick={handleShowReport}
          disabled={loading}
          sx={{ minHeight: 48 }}
        >
          {loading ? "Loading..." : "View Report"}
        </Button>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => setExportDialogOpen(true)}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Generate Report
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Generate Daily Attendance Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose the file format for the selected filters.
          </Typography>
          <Box display="grid" gap={1.25}>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => { setExportDialogOpen(false); handleExport("pdf"); }}
              sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 700, backgroundColor: "#c0392b", color: "#fff", "&:hover": { backgroundColor: "#a93226" } }}
            >
              PDF Document
            </Button>
            <Button
              variant="contained"
              startIcon={<GridOnIcon />}
              onClick={() => { setExportDialogOpen(false); handleExport("xlsx"); }}
              sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 700, backgroundColor: "#217346", color: "#fff", "&:hover": { backgroundColor: "#185c37" } }}
            >
              Excel Workbook
            </Button>
            <Button
              variant="contained"
              startIcon={<TableViewIcon />}
              onClick={() => { setExportDialogOpen(false); handleExport("csv"); }}
              sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 700, backgroundColor: "#246b8f", color: "#fff", "&:hover": { backgroundColor: "#1b526e" } }}
            >
              CSV File
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyAttendanceReport;
